// Order a Test — pure, React/router-free helpers split out of OrderTest.js
// on purpose, twice over now:
//   1. distinctFamilySchemas already needed to be unit-testable on its
//      own (see its own comment below).
//   2. 2026-10-09 bug fix added a second pure function (effectiveSelectedTests)
//      that ALSO needs direct unit coverage, and importing OrderTest.js
//      itself to get at either one pulls in react-router-dom, which this
//      project's installed Jest/react-scripts version fails to resolve
//      (pre-existing environment issue — see orderTestApiBase.test.js's
//      own header comment for the same failure mode). Living in their own
//      module with zero React/router imports means OrderTest.test.js can
//      actually import and run these again instead of failing at the
//      require step.
//
// OrderTest.js imports everything below from here; nothing in this file
// is redefined there anymore.

export const PROOVE_PGX_SCHEMA_KEY = "proove_pgx_v1";
export const TOXICOLOGY_SCHEMA_KEY = "toxicology_v1";
export const INFECTIOUS_DISEASE_SCHEMA_KEY = "infectious_disease_molecular_v1";

// The PGx requisition covers all 3 Proove profiles in one form (confirmed
// design — the real paper form allows ordering any combination). Selecting
// a specific test on Step 1 pre-checks its matching profile in Step 3/4;
// the clinician can still add/remove others before submitting, same as
// the real form allows — see effectiveSelectedTests below for the fix
// that makes that actually stick.
export const TEST_ID_TO_PROFILE_PREFIX = {
  proove_pgx: "drug_metabolism",
  proove_opioid_risk: "opioid_risk",
  proove_pain_perception: "pain_perception",
};

const PROFILE_PREFIX_TO_TEST_ID = Object.fromEntries(
  Object.entries(TEST_ID_TO_PROFILE_PREFIX).map(([testId, prefix]) => [prefix, testId])
);

// A checkbox_group field's value is an array of option codes (see
// DynamicRequisitionForm.js's CheckboxGroupField — `toggle` always calls
// onChange with an array, never deletes the key). "Checked" means the
// array contains "selected", NOT merely that the key exists: unchecking
// a box sets it back to `[]`, it never removes the field from
// clinicalDataByFamily. This intentionally mirrors labariumaireport's
// own validate_order_required, which treats `any(submitted_data.get(fid))`
// as the "is this one ordered" test — `bool([])` is falsy in both Python
// and JS, so client and server agree on what "checked" means.
function isProfileChecked(value) {
  return Array.isArray(value) ? value.length > 0 : !!value;
}

// Order a Test, Part B — dedupe selected tests down to the distinct
// requisition families/schemas actually in play, preserving the order
// each family was first selected in. Exported as a pure function (no
// component state) specifically so it's unit-testable on its own — see
// OrderTest.test.js for its actual coverage.
export function distinctFamilySchemas(selectedTests) {
  const seen = new Map();
  for (const t of selectedTests) {
    const key = t.requisition_schema_key;
    if (!key) continue;
    if (!seen.has(key)) {
      seen.set(key, { schemaKey: key, family: t.family || "proove", testIds: [t.test_id] });
    } else {
      seen.get(key).testIds.push(t.test_id);
    }
  }
  return [...seen.values()];
}

// Bug fix, 2026-10-09 — a real paper-form behavior Step 3/4 correctly
// allows (checking Pain Perception even though only Opioid Risk + Drug
// Metabolism were picked in Step 0, or unchecking one that WAS picked in
// Step 0) was never reconciled back into the order: Step 0's
// `selectedTests` stayed the SOLE source of truth for both the Review
// summary and the submitted `test_ids`, so anything checked/unchecked
// only inside the form silently never reached either one. The
// requisition's own checkboxes are the clinically authoritative record
// of what's actually being ordered (it's what gets signed and sent to
// the lab) — this derives the EFFECTIVE Proove test_ids from them, in
// both directions, via TEST_ID_TO_PROFILE_PREFIX.
//
// Returns { tests, missingTestIds }:
//   - `tests` is the effective selection: every non-Proove test
//     untouched, plus exactly one entry per CHECKED Proove profile
//     (not per originally-selected one), inserted at the position the
//     first Proove test originally occupied in `selectedTests` — so
//     family order for distinctFamilySchemas/the backend's own
//     families_by_schema (which both preserve first-seen order) doesn't
//     shift just because a profile was added/removed inside the form.
//   - `missingTestIds` lists any checked profile whose test_id couldn't
//     be resolved to an actual test object (not currently selected AND
//     not found in the full catalog, e.g. deactivated mid-order) —
//     callers must surface this as an error, never drop it silently;
//     that's the exact failure mode this fix exists to close.
// 2026-10-09, part 2 — the founder flagged the SAME reconciliation
// principle (whatever's actually checked/selected inside the
// requisition's own clinical-details form is authoritative, not just
// whatever Step 0 originally picked) must hold for EVERY family —
// Toxicology and Infectious Disease too, not just Proove. The two
// families are structurally different from Proove's `profile_selection`
// checkbox group (3 independent boxes, each 1:1 with a real test_id via
// TEST_ID_TO_PROFILE_PREFIX): order_requisition_schema.py's
// `toxicology_screen_type`/`infectious_disease_test_type` fields are
// `single_select` (at most ONE value, not several at once), and —
// crucially — their option `code`s ARE the real catalog test_ids
// directly (tox_placeholder_1/2, id_molecular_placeholder_1/2; confirmed
// against labariumaiportal/backend/main.py's catalog seed), unlike
// Proove's checkbox options whose code is always the fixed string
// "selected", translated to a test_id only via TEST_ID_TO_PROFILE_PREFIX.
//
// That difference is exactly why this generalizes WITHOUT a second
// hardcoded per-family constant: for any family other than Proove, the
// test_id <-> field/option relationship can be read directly off the
// already-fetched schema (schemasByKey[schemaKey], a section/field list)
// by scanning for a single_select or checkbox_group field whose option
// `code` equals a real catalog test_id for that schema. A future 4th
// family built the same way (option code === test_id) reconciles for
// free, no new code here — only Proove, whose option codes are NOT
// test_ids, needs its own dedicated resolver below.
function schemaDrivenTestIdFieldMap(schemaSections, testIdSet) {
  const map = new Map();
  for (const section of schemaSections || []) {
    for (const field of section.fields || []) {
      if (field.type !== "single_select" && field.type !== "checkbox_group") continue;
      for (const opt of field.options || []) {
        if (testIdSet.has(opt.code)) {
          map.set(opt.code, { fieldId: field.id, optionCode: opt.code, kind: field.type });
        }
      }
    }
  }
  return map;
}

// Proove's own resolver — unchanged logic from the original fix, just
// pulled into its own function so the outer effectiveSelectedTests can
// dispatch to it as one family-specific case among others, instead of
// being the only path that exists.
function effectiveProoveTestIds(clinicalDataByFamily) {
  const proofData = (clinicalDataByFamily && clinicalDataByFamily[PROOVE_PGX_SCHEMA_KEY]) || {};
  return Object.values(TEST_ID_TO_PROFILE_PREFIX)
    .filter((prefix) => isProfileChecked(proofData[`${prefix}_selected`]))
    .map((prefix) => PROFILE_PREFIX_TO_TEST_ID[prefix]);
}

// The generic, schema-driven resolver every OTHER family goes through.
// Returns the effective test_ids for one schema_key, given:
//   - which of its test_ids Step 0 originally selected (`originalIds`),
//   - the live clinicalDataByFamily[schemaKey] bucket,
//   - the already-fetched schema section list for this schema_key.
//
// Three cases per test_id a schema field actually maps to a test_id for:
//   - the mapped field was never touched (key absent from clinicalData at
//     all, e.g. the form hasn't rendered yet / clinician hasn't opened
//     this section) — defer to whatever Step 0 originally selected, same
//     "untouched form = Step 0 stays authoritative" rule Proove's own
//     pre-check effect relies on;
//   - single_select: effectively selected iff the field's CURRENT value
//     equals this test_id's option code (at most one test_id per field
//     can win, by construction — this is the "different in kind from
//     Proove's checkbox group" case the founder flagged);
//   - checkbox_group: effectively selected iff the field's current array
//     value contains this test_id's option code (same semantics as
//     isProfileChecked/Proove, generalized).
// Any test_id the schema has NO mapped field for at all (fieldMap empty,
// or this specific test_id isn't in it) passes through untouched — never
// silently dropped just because this family hasn't been reconciled yet.
function effectiveGenericTestIds(schemaKey, selectedTests, clinicalDataByFamily, allTests, schemasByKey) {
  const originalIdsForSchema = selectedTests
    .filter((s) => s.requisition_schema_key === schemaKey)
    .map((s) => s.test_id);

  const catalogIdsForSchema = new Set([
    ...(allTests || []).filter((c) => c.requisition_schema_key === schemaKey).map((c) => c.test_id),
    ...originalIdsForSchema,
  ]);
  const schemaSections = (schemasByKey && schemasByKey[schemaKey]) || [];
  const fieldMap = schemaDrivenTestIdFieldMap(schemaSections, catalogIdsForSchema);

  if (fieldMap.size === 0) {
    // Schema not loaded yet, or (any future family) has no field at all
    // whose option codes are test_ids — nothing to reconcile, so Step 0's
    // original selection for this family stands exactly as before this
    // whole generalization existed.
    return originalIdsForSchema;
  }

  const reconciledIds = new Set(fieldMap.keys());
  const passthroughIds = originalIdsForSchema.filter((id) => !reconciledIds.has(id));

  // Group by fieldId — a single_select's several options share ONE field/
  // ONE current value; a checkbox_group's options are each independently
  // checked.
  const byField = new Map();
  for (const [testId, meta] of fieldMap.entries()) {
    if (!byField.has(meta.fieldId)) byField.set(meta.fieldId, []);
    byField.get(meta.fieldId).push({ testId, ...meta });
  }

  const clinicalData = (clinicalDataByFamily && clinicalDataByFamily[schemaKey]) || {};
  const originalIdSet = new Set(originalIdsForSchema);
  const selected = [];
  for (const [fieldId, entries] of byField.entries()) {
    const touched = Object.prototype.hasOwnProperty.call(clinicalData, fieldId);
    if (!touched) {
      entries.forEach(({ testId }) => { if (originalIdSet.has(testId)) selected.push(testId); });
      continue;
    }
    const value = clinicalData[fieldId];
    entries.forEach(({ testId, optionCode, kind }) => {
      if (kind === "single_select") {
        if (value === optionCode) selected.push(testId);
      } else if (isProfileChecked(value) && Array.isArray(value) && value.includes(optionCode)) {
        selected.push(testId);
      }
    });
  }
  return [...passthroughIds, ...selected];
}

export function effectiveSelectedTests(selectedTests, clinicalDataByFamily, allTests, schemasByKey) {
  const catalog = allTests || [];
  const resolveTestObj = (testId) =>
    selectedTests.find((s) => s.test_id === testId) || catalog.find((s) => s.test_id === testId);

  const result = [];
  const missingTestIds = [];
  const placedSchemaKeys = new Set();

  for (const t of selectedTests) {
    const schemaKey = t.requisition_schema_key;
    if (!schemaKey) { result.push(t); continue; }
    if (placedSchemaKeys.has(schemaKey)) continue; // this family's group was already inserted at its first slot

    placedSchemaKeys.add(schemaKey);
    const effectiveIds = schemaKey === PROOVE_PGX_SCHEMA_KEY
      ? effectiveProoveTestIds(clinicalDataByFamily)
      : effectiveGenericTestIds(schemaKey, selectedTests, clinicalDataByFamily, catalog, schemasByKey);

    effectiveIds.forEach((testId) => {
      const obj = resolveTestObj(testId);
      if (obj) result.push(obj);
      else missingTestIds.push(testId);
    });
  }
  return { tests: result, missingTestIds };
}

// UX counterpart to effectiveGenericTestIds, for the SAME reason Proove's
// own pre-check effect exists (OrderTest.js: "selecting a specific
// profile pre-checks it"): a test_id picked in Step 0 should show up
// already selected inside its family's Step 3/4 form, not just silently
// remain effective-by-default via the "untouched field" fallback above.
// Returns { [schemaKey]: { [fieldId]: value } } — ONLY for fields not
// already present in that family's current clinicalDataByFamily bucket
// (an OrderTest.js caller merges these in without ever overwriting a
// field the clinician already touched, exactly like Proove's own effect
// never un-checks a profile the clinician unchecked by hand). Proove
// itself is handled by OrderTest.js's existing dedicated effect, unchanged
// — this only covers every OTHER family, schema-driven, same as
// effectiveGenericTestIds.
export function genericFamilyPreCheckUpdates(selectedTests, schemasByKey, currentClinicalDataByFamily) {
  const schemas = schemasByKey || {};
  const current = currentClinicalDataByFamily || {};
  const updates = {};
  const schemaKeys = new Set(
    selectedTests.map((t) => t.requisition_schema_key).filter((k) => k && k !== PROOVE_PGX_SCHEMA_KEY)
  );
  for (const schemaKey of schemaKeys) {
    const schemaSections = schemas[schemaKey];
    if (!schemaSections) continue;
    const idsForSchema = selectedTests
      .filter((t) => t.requisition_schema_key === schemaKey)
      .map((t) => t.test_id);
    const fieldMap = schemaDrivenTestIdFieldMap(schemaSections, new Set(idsForSchema));
    if (fieldMap.size === 0) continue;

    const alreadySet = current[schemaKey] || {};
    const fieldUpdates = {};
    for (const testId of idsForSchema) {
      const meta = fieldMap.get(testId);
      if (!meta) continue;
      if (Object.prototype.hasOwnProperty.call(alreadySet, meta.fieldId)) continue; // never clobber a touched field
      if (meta.kind === "single_select") {
        if (!(meta.fieldId in fieldUpdates)) fieldUpdates[meta.fieldId] = meta.optionCode;
      } else {
        fieldUpdates[meta.fieldId] = [...new Set([...(fieldUpdates[meta.fieldId] || []), meta.optionCode])];
      }
    }
    if (Object.keys(fieldUpdates).length > 0) updates[schemaKey] = fieldUpdates;
  }
  return updates;
}

// Review/submit-time guard mirroring labariumaireport's own
// REQUIRE_ONE_OF_PROFILE_FIELDS rule, client-side. Needed because that
// server-side rule lives inside validate_order_required, which only ever
// runs for a schema_key that actually gets rendered — and
// labariumaiportal's families_by_schema is built FROM test_ids (see
// main.py's submit_clinician_order). If every Proove profile ends up
// unchecked, effectiveSelectedTests correctly drops Proove's test_ids
// entirely, which means proove_pgx_v1 is never rendered at all and
// validate_order_required never runs for it — so without this guard:
//   - Proove-only order: test_ids collapses to [], and the clinician
//     gets the generic "At least one test must be selected" 422 instead
//     of the specific profile message.
//   - Proove + another family: the order SILENTLY submits as just the
//     other family — the exact bug being fixed, in the opposite
//     direction, with no error at all.
// So this is checked and blocked client-side before submit, not left to
// the server to catch.
export function proovePresentWithNoProfileChecked(selectedTests, clinicalDataByFamily) {
  const proovePresent = selectedTests.some((t) => TEST_ID_TO_PROFILE_PREFIX[t.test_id]);
  if (!proovePresent) return false;
  const proofData = (clinicalDataByFamily && clinicalDataByFamily[PROOVE_PGX_SCHEMA_KEY]) || {};
  return !Object.values(TEST_ID_TO_PROFILE_PREFIX).some((prefix) => isProfileChecked(proofData[`${prefix}_selected`]));
}

// Byte-identical to order_requisition_schema.py's own
// _REQUIRE_ONE_OF_BY_SCHEMA[PROOVE_PGX_SCHEMA_KEY] message, so a
// clinician who somehow still reaches the server-side 422 (e.g. the
// client-side guard above was bypassed) sees the exact same wording
// either way.
export const PROOVE_NO_PROFILE_CHECKED_MESSAGE =
  "At least one profile (Opioid Risk, Drug Metabolism, or Pain Perception) must be ordered.";

// 2026-10-14 fix — real gap found by independent review: the generalized
// guard below (familyPresentWithNothingRequiredSelected) only fires for a
// family whose test_id-mapped selection field is itself marked
// `required: true` in the schema. Toxicology's and Infectious Disease's
// REAL one-of groups the server enforces — order_requisition_schema.py's
// own _REQUIRE_ONE_OF_BY_SCHEMA[TOXICOLOGY_SCHEMA_KEY]/
// [INFECTIOUS_DISEASE_SCHEMA_KEY] — are a DIFFERENT set of fields
// (poc_results/non_poc_urine_options/non_poc_oral_options/
// additional_drug_confirmations, and pcr_panels/reference_tests) than the
// test_id-mapped single-select fields the generic guard checks
// (toxicology_screen_type/infectious_disease_test_type, both deliberately
// still `required: False` today). So a clinician could select a
// toxicology or ID test, leave every option in the server's actual
// one-of groups empty, pass every client-side check, and only discover
// the problem at the server's generic 422 on submit. Byte-identical field
// lists and messages to the backend's own _REQUIRE_ONE_OF_BY_SCHEMA — if
// either ever changes there, update here too (these are hand-kept in
// sync, not imported, same as PATIENT_RECORD_CLINICAL_FIELD_IDS above).
const _TOXICOLOGY_ONE_OF_FIELDS = [
  "poc_results", "non_poc_urine_options", "non_poc_oral_options", "additional_drug_confirmations",
];
export const TOXICOLOGY_NO_OPTION_SELECTED_MESSAGE =
  "At least one toxicology test option (POC results, a non-POC screen, or an additional drug confirmation "
  + "by class) must be selected.";

const _INFECTIOUS_DISEASE_ONE_OF_FIELDS = ["pcr_panels", "reference_tests"];
export const INFECTIOUS_DISEASE_NO_OPTION_SELECTED_MESSAGE =
  "At least one infectious disease PCR panel or reference test must be selected.";

// Same truthy-value check the backend uses (`any(submitted_data.get(fid)
// for fid in fids)`) — a non-empty array/string is truthy, matching how
// checkbox_group/multi_select fields are actually stored client-side.
function _anyFieldTruthy(clinicalData, fieldIds) {
  return fieldIds.some((fid) => {
    const v = clinicalData[fid];
    return Array.isArray(v) ? v.length > 0 : Boolean(v);
  });
}

function _schemaPresentWithNoOneOfFieldSet(selectedTests, clinicalDataByFamily, schemaKey, fieldIds) {
  const present = selectedTests.some((t) => t.requisition_schema_key === schemaKey);
  if (!present) return false;
  const clinicalData = (clinicalDataByFamily && clinicalDataByFamily[schemaKey]) || {};
  return !_anyFieldTruthy(clinicalData, fieldIds);
}

// Exported, named convenience wrappers (matching proovePresentWithNoProfileChecked's
// own naming convention) so OrderTest.js's specific-message lookup doesn't
// need to know the actual field-id lists itself.
export function toxicologyPresentWithNoOptionSelected(selectedTests, clinicalDataByFamily) {
  return _schemaPresentWithNoOneOfFieldSet(selectedTests, clinicalDataByFamily, TOXICOLOGY_SCHEMA_KEY, _TOXICOLOGY_ONE_OF_FIELDS);
}

export function infectiousDiseasePresentWithNoOptionSelected(selectedTests, clinicalDataByFamily) {
  return _schemaPresentWithNoOneOfFieldSet(selectedTests, clinicalDataByFamily, INFECTIOUS_DISEASE_SCHEMA_KEY, _INFECTIOUS_DISEASE_ONE_OF_FIELDS);
}

// 2026-10-09, part 2 — the generalized form of the guard above. Proove's
// own rule (REQUIRE_ONE_OF_PROFILE_FIELDS) isn't driven by any single
// field's `required` flag, so it keeps its own dedicated branch,
// unchanged. Every OTHER family's guard is schema-driven instead: block
// submission only when (a) this family IS present in the order, (b) its
// schema has a field whose option codes map 1:1 to its catalog test_ids
// (the same fieldMap effectiveGenericTestIds builds), (c) that field is
// itself marked `required: true` in the fetched schema, and (d) nothing
// currently resolves to "selected" for it.
//
// As of this pass (2026-10-09), Toxicology's `toxicology_screen_type` and
// Infectious Disease's `infectious_disease_test_type` are BOTH still
// `required: False` in order_requisition_schema.py — deliberately, since
// the real test-name mapping behind the placeholder options is still
// pending external confirmation (see that file's own comments on those
// two fields) — so this correctly does NOT fire for either family today.
// The moment either field is flipped to `required: True`, this starts
// blocking automatically, with no code change needed here.
// 2026-10-09 — billing/demographic autofill from a returning patient's
// most recent previous order (GET /clinician/patients/{id}/last-order-data).
// Two pure helpers, pulled out for the same reason every other helper in
// this file is pure: OrderTest.test.js can unit-test the actual merge/strip
// logic directly, without needing react-router-dom (see this file's own
// header comment) or a full stepper walk-through.
//
// 2026-10-10 — SUPERSEDED as the primary source: clinician_patient_records
// itself now carries this exact field set directly (backend's
// _PATIENT_RECORD_CLINICAL_FIELD_IDS), kept current automatically by every
// order's two-way sync and directly editable in My Patients — that's the
// new authoritative source, already present on the patient object OrderTest
// fetches at Step 1/2 (no extra round-trip). The last-order-data endpoint
// is kept ONLY as a fallback for a patient whose record predates this
// change (real order history, but the record's own fields are still
// empty). PATIENT_RECORD_CLINICAL_FIELD_IDS below is the SAME list the
// backend uses — kept in sync by hand since this is a separate app/
// language from main.py, not an import.
export const PATIENT_RECORD_CLINICAL_FIELD_IDS = [
  "sex", "height_ft", "height_in", "weight_lbs", "race", "middle_initial",
  "bill_to", "policy_number", "group_number", "insurance_provider", "subscriber_name", "relationship",
];

// Same "is this field actually filled in" predicate mergeLastOrderPrefill
// already uses below, exported standalone so this and other callers share
// exactly one definition of "empty" for these fields.
function isFieldEmpty(value) {
  return value === undefined || value === null || value === ""
    || (Array.isArray(value) && value.length === 0);
}

// Order a Test, Step 2's autofill-source decision: given the just-selected
// patient object (already fetched at Step 1/2 — the same `p` passed to
// selectPatient in OrderTest.js), decide whether its OWN
// PATIENT_RECORD_CLINICAL_FIELD_IDS are usable directly (no extra fetch
// needed) or whether OrderTest.js must fall back to the last-order-data
// endpoint for a pre-2026-10-10 patient whose record was never synced.
//
// Returns { source: "record", data } when ANY of the patient's own
// clinical fields is non-empty — the record is authoritative the moment it
// has ANYTHING, never partially topped up from a stale previous order
// (that would resurrect a value the clinician deliberately cleared in My
// Patients). `data` is exactly the non-empty subset, pre-filtered to this
// field set only (never an identity field, even though they live on the
// same object).
//
// Returns { source: "last_order" } when EVERY one of these fields is
// empty/unset on the patient record — nothing to use yet, so the caller
// should fall back to GET /clinician/patients/{id}/last-order-data.
export function patientRecordAutofillSource(patient) {
  const data = {};
  for (const fid of PATIENT_RECORD_CLINICAL_FIELD_IDS) {
    const v = patient ? patient[fid] : undefined;
    if (!isFieldEmpty(v)) data[fid] = v;
  }
  if (Object.keys(data).length > 0) return { source: "record", data };
  return { source: "last_order" };
}

// Merges `data` (the endpoint's shared-section fields for the JUST-selected
// patient) into `current` (sharedClinicalData), but ONLY into fields that
// are still empty/unset in `current` — never overwrites anything the
// clinician already typed/selected while the fetch was in flight. Returns
// { merged, appliedFields }: `merged` is a new object (or `current` itself,
// unchanged, if nothing was empty) and `appliedFields` is the subset of
// `data` that was actually written, keyed the same way — the caller needs
// this exact subset later (see stripAppliedPrefill) to be able to tell
// "value the clinician typed" apart from "value this prefill injected and
// nothing has touched since."
export function mergeLastOrderPrefill(current, data) {
  const merged = { ...(current || {}) };
  const appliedFields = {};
  for (const [k, v] of Object.entries(data || {})) {
    const existing = merged[k];
    const isEmpty = existing === undefined || existing === null || existing === ""
      || (Array.isArray(existing) && existing.length === 0);
    if (isEmpty) {
      merged[k] = v;
      appliedFields[k] = v;
    }
  }
  return { merged, appliedFields };
}

// Removes, from `current`, any field in `appliedFields` whose value in
// `current` is STILL exactly what the prefill wrote (the clinician never
// touched it). Used when the clinician moves on to a different patient (or
// to "+ New Patient") before submitting — a value this feature injected for
// patient A must never silently ride along into patient B's order just
// because B has no prior-order data of its own to overwrite it with. A
// field the clinician DID edit after the prefill landed is left alone,
// exactly as typed — this only ever removes what the clinician never
// actually looked at. Deep-compares with JSON.stringify since some of
// these fields (`race`) are arrays, not primitives.
export function stripAppliedPrefill(current, appliedFields) {
  if (!appliedFields || Object.keys(appliedFields).length === 0) return current;
  let changed = false;
  const next = { ...(current || {}) };
  for (const [k, v] of Object.entries(appliedFields)) {
    if (k in next && JSON.stringify(next[k]) === JSON.stringify(v)) {
      delete next[k];
      changed = true;
    }
  }
  return changed ? next : current;
}

export function familyPresentWithNothingRequiredSelected(selectedTests, clinicalDataByFamily, schemasByKey) {
  if (proovePresentWithNoProfileChecked(selectedTests, clinicalDataByFamily)) return true;
  if (_schemaPresentWithNoOneOfFieldSet(selectedTests, clinicalDataByFamily, TOXICOLOGY_SCHEMA_KEY, _TOXICOLOGY_ONE_OF_FIELDS)) return true;
  if (_schemaPresentWithNoOneOfFieldSet(selectedTests, clinicalDataByFamily, INFECTIOUS_DISEASE_SCHEMA_KEY, _INFECTIOUS_DISEASE_ONE_OF_FIELDS)) return true;

  const schemas = schemasByKey || {};
  const schemaKeys = new Set(
    selectedTests
      .map((t) => t.requisition_schema_key)
      .filter((k) => k && k !== PROOVE_PGX_SCHEMA_KEY)
  );

  for (const schemaKey of schemaKeys) {
    const schemaSections = schemas[schemaKey];
    if (!schemaSections) continue; // schema not fetched yet — nothing to check yet

    const catalogIdsForSchema = new Set(
      selectedTests.filter((t) => t.requisition_schema_key === schemaKey).map((t) => t.test_id)
    );
    const fieldMap = schemaDrivenTestIdFieldMap(schemaSections, catalogIdsForSchema);
    if (fieldMap.size === 0) continue; // this family has no test_id-mapped selection field at all

    const requiredFieldIds = new Set();
    for (const section of schemaSections) {
      for (const field of section.fields || []) {
        if (field.required && (field.type === "single_select" || field.type === "checkbox_group")) {
          requiredFieldIds.add(field.id);
        }
      }
    }
    const relevantRequiredFieldIds = [...new Set([...fieldMap.values()].map((m) => m.fieldId))]
      .filter((fid) => requiredFieldIds.has(fid));
    if (relevantRequiredFieldIds.length === 0) continue; // not required (yet) for this family

    const clinicalData = (clinicalDataByFamily && clinicalDataByFamily[schemaKey]) || {};
    const anySelected = [...fieldMap.values()].some(({ fieldId, optionCode, kind }) => {
      const value = clinicalData[fieldId];
      if (kind === "single_select") return value === optionCode;
      return isProfileChecked(value) && Array.isArray(value) && value.includes(optionCode);
    });
    if (!anySelected) return true;
  }
  return false;
}
